<script setup lang="ts">
import {
    BBtnIcon,
    BIcon,
    BList,
    BListItem,
    BListItemTitle,
    BMenu,
    BSkeletonLoader,
    BProgressCircular,
} from '@/design-system';
import { useConfirmation, useTranslation } from '@/composables';
import { useChatbotStore } from '@/stores/chatbotStore';
import type { ChatbotConversationType } from '@/types/ChatbotConversationType';

withDefaults(
    defineProps<{
        expanded?: boolean;
    }>(),
    { expanded: false },
);

const emit = defineEmits<{
    conversationSelect: [conversation: ChatbotConversationType];
    newConversation: [];
}>();

const { t } = useTranslation();
const { onConfirm } = useConfirmation();
const chatbotStore = useChatbotStore();

const handleConversationSelect = (conversation: ChatbotConversationType): void => {
    emit('conversationSelect', conversation);
};

const handleDeleteConversation = async (conversationId: string | number, event: Event): Promise<void> => {
    event.stopPropagation();
    const confirmed = await onConfirm(t('chatbot.deleteConversationTitle'), t('chatbot.confirmDeleteConversation'));
    if (confirmed) {
        await chatbotStore.deleteConversation(conversationId);
    }
};

const isActiveConversation = (conversation: ChatbotConversationType): boolean => {
    return chatbotStore.currentConversation?.id === conversation.id;
};

const isDeletingConversation = (conversation: ChatbotConversationType): boolean => {
    return chatbotStore.isDeletingConversationId === conversation.id;
};

const handleToggleExpanded = (): void => {
    chatbotStore.toggleSidebarExpanded();
};

const handleNewChat = (): void => {
    emit('newConversation');
};
</script>

<template>
    <div
        class="chat-history-sidebar d-flex flex-column"
        :class="{ 'sidebar-expanded': expanded, 'sidebar-collapsed': !expanded }"
    >
        <div class="sidebar-header pl-6 pr-4 py-3 pt-6">
            <div
                class="d-flex align-center"
                :class="expanded ? 'justify-space-between' : 'justify-center'"
            >
                <div
                    v-if="expanded"
                    class="d-flex align-center"
                >
                    <BIcon
                        size="15"
                        class="mr-2"
                        color="data-table-content"
                    >mdi-message-text</BIcon
                    >
                    <span class="text-body-1 text-data-table-actions">{{ t('chatbot.chats') }}</span>
                </div>
                <div class="d-flex align-center">
                    <BBtnIcon
                        :icon="expanded ? 'mdi-chevron-left' : 'mdi-chevron-right'"
                        color="data-table-content"
                        :tooltip="expanded ? t('chatbot.minimizeChat') : t('chatbot.maximizeChat')"
                        @click="handleToggleExpanded"
                    />
                    <BBtnIcon
                        icon="mdi-plus"
                        :tooltip="t('chatbot.newChat')"
                        color="data-table-content"
                        @click="handleNewChat"
                    />
                </div>
            </div>
        </div>

        <div
            v-if="expanded"
            class="conversation-list flex-grow-1"
        >
            <div
                v-if="chatbotStore.isLoadingConversations"
                class="pa-4"
            >
                <BSkeletonLoader type="list-item, list-item, list-item, list-item" />
            </div>
            <div
                v-else-if="chatbotStore.sortedConversations.length === 0"
                class="empty-state pa-4 text-center"
            >
                <BIcon
                    size="48"
                    color="grey-400"
                    class="mb-2"
                >
                    mdi-chat-outline
                </BIcon>
                <p class="text-grey-600 text-body-2">
                    {{ t('chatbot.noConversations') }}
                </p>
            </div>

            <BList
                v-else
                class="conversation-list-items"
            >
                <BListItem
                    v-for="conversation in chatbotStore.sortedConversations"
                    :key="conversation.id"
                    density="compact"
                    rounded="xl"
                    class="conversation-item"
                    :class="{
                        'conversation-item--active': isActiveConversation(conversation),
                        'conversation-item--deleting': isDeletingConversation(conversation),
                    }"
                    :disabled="isDeletingConversation(conversation)"
                    @click="handleConversationSelect(conversation)"
                >
                    <BListItemTitle class="conversation-title"> {{ conversation.title }}... </BListItemTitle>

                    <template #append>
                        <BProgressCircular
                            v-if="isDeletingConversation(conversation)"
                            size="20"
                            width="2"
                            indeterminate
                            color="error"
                        />
                        <BMenu
                            v-else
                            activator-btn-classes="conversation-menu-btn"
                            activator-icon="mdi-dots-vertical"
                            activator-btn-size="small"
                            activator-btn-density="compact"
                            :activator-btn-rounded="true"
                        >
                            <template #list-items>
                                <BListItem
                                    density="compact"
                                    @click="handleDeleteConversation(conversation.id, $event)"
                                >
                                    <BListItemTitle class="d-flex align-center gc-2">
                                        <BIcon
                                            size="small"
                                            color="error"
                                        >mdi-delete-outline</BIcon
                                        >
                                        {{ t('common.delete') }}
                                    </BListItemTitle>
                                </BListItem>
                            </template>
                        </BMenu>
                    </template>
                </BListItem>
            </BList>
        </div>
    </div>
</template>

<style scoped lang="scss">
.chat-history-sidebar {
    position: fixed;
    top: 60px;
    left: calc(1px * var(--v-sidebar-width));
    width: 280px;
    height: 100%;
    transition: width 0.3s ease;
    z-index: 1000;

    &.sidebar-collapsed {
        width: 60px;
        cursor: pointer;
    }

    &.sidebar-expanded {
        width: 280px;
    }
}

.sidebar-header {
    flex-shrink: 0;
    background-color: rgba(var(--v-theme-surface), 0.95);
}

.search-input {
    :deep(.v-field__input) {
        font-size: 0.875rem;
    }
}

.conversation-list {
    background-color: rgba(var(--v-theme-surface), 0.95);
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(var(--v-theme-on-surface), 0.2);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: rgba(var(--v-theme-on-surface), 0.3);
    }
}

.conversation-list-items {
    padding: 0;
}

.conversation-item {
    margin-inline: 10px;
    margin-bottom: 4px;
    padding: 8px 20px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgba(var(--v-theme-on-surface), 0.04);
    }

    &--active {
        background-color: #f1f1f2;

        &:hover {
            background-color: rgba(var(--v-theme-secondary), 0.12);
        }
    }

    &--deleting {
        opacity: 0.6;
        pointer-events: none;
    }

    :deep(.v-list-item__content) {
        flex: 1;
        overflow: hidden;
    }
}

.conversation-title {
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.2;
    color: rgba(var(--v-theme-data-table-actions));

    :deep(.v-list-item-title) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.conversation-preview {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.7);
    line-height: 1.2;
    margin-bottom: 4px;

    :deep(.v-list-item-subtitle) {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

:deep(.conversation-menu-btn) {
    opacity: 0;
    transition: opacity 0.2s ease;
}

:deep(.conversation-item:hover .conversation-menu-btn),
:deep(.conversation-menu-btn[aria-expanded='true']) {
    opacity: 1;
}

.empty-state {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
}
</style>
